import { useEffect, useState } from 'react'

import { Stack, Surface, Text } from '@taro/design'

import type { Proof as ProofData } from '../../api'
import { recompute, type Recomputed } from '../../verify'
import styles from './reading.module.css'

interface Props {
  proof: ProofData
  onBack: () => void
}

/**
 * Экран проверки честности.
 *
 * Единственное место продукта, где обещание «мы не подкручиваем» становится
 * проверяемым. Поэтому здесь принципиально не показывается поле `valid`,
 * пришедшее с сервера: проверка, которую делает проверяемый, ничего не
 * проверяет. Всё, что видно ниже, пересчитано в этом телефоне — см. verify.ts.
 */
export function Proof({ proof, onBack }: Props) {
  const [result, setResult] = useState<Recomputed | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    recompute(proof).then(
      (r) => live && setResult(r),
      () => live && setFailed(true),
    )
    return () => {
      live = false
    }
  }, [proof])

  const ok = result?.hashOk === true && result.cardsOk

  return (
    <Stack gap={5}>
      <button type="button" className={styles.back} onClick={onBack}>
        ← К раскладу
      </button>

      <Stack gap={2}>
        <Text variant="display-m" as="h2">
          Проверка
        </Text>
        <Text variant="body-m" tone="tertiary" measure>
          Порядок карт был предопределён до того, как ты коснулся экрана, —
          и мы это доказали заранее, показав отпечаток. Подобрать другой
          набор карт под тот же отпечаток невозможно.
        </Text>
      </Stack>

      <ol className={styles.steps}>
        <Step n={1} title="До тасовки сервер показал отпечаток">
          <code className={styles.hash}>{proof.serverSeedHash}</code>
          <Text variant="caption" tone="tertiary">
            Ты видел его на экране с веером — до того, как выбрал карты.
          </Text>
        </Step>

        <Step n={2} title="Ты перетасовал колоду">
          <code className={styles.hash}>{proof.clientSeed}</code>
          <Text variant="caption" tone="tertiary">
            Свёрнутая траектория пальца. Сервер не знал её, когда фиксировал
            свою половину.
          </Text>
        </Step>

        <Step n={3} title="После раздачи сервер раскрыл секрет">
          <code className={styles.hash}>{proof.serverSeed}</code>
        </Step>

        <Step n={4} title="Телефон пересчитал всё заново">
          {failed ? (
            <Text variant="body-s" tone="danger">
              Не удалось пересчитать в этом браузере.
            </Text>
          ) : result === null ? (
            <Text variant="body-s" tone="tertiary">
              Считаем…
            </Text>
          ) : (
            <Stack gap={2}>
              <Check ok={result.hashOk}>Отпечаток соответствует раскрытому секрету</Check>
              <Check ok={result.cardsOk}>Все карты лежат там, где велит расчёт</Check>
              {result.mismatches.map((m) => (
                <Text key={m} variant="caption" tone="danger">
                  {m}
                </Text>
              ))}
            </Stack>
          )}
        </Step>
      </ol>

      {result && !ok ? (
        <Surface level="raised" className={styles.verdictBad}>
          <Text variant="title-s">Расклад не сходится</Text>
          <Text variant="body-s" tone="tertiary" measure>
            Это ошибка на нашей стороне, и мы уже о ней знаем. Напиши нам —
            такой случай разбирают вручную.
          </Text>
        </Surface>
      ) : null}

      <Stack gap={2}>
        <Text variant="overline" tone="tertiary">
          Что выпало и почему
        </Text>
        <table className={styles.proofTable}>
          <thead>
            <tr>
              <th>Место в веере</th>
              <th>Карта в колоде</th>
              <th>Выпала</th>
            </tr>
          </thead>
          <tbody>
            {proof.cards.map((c) => (
              <tr key={c.fanSlot}>
                <td>{c.fanSlot + 1}</td>
                <td>№{c.deckIdx + 1}</td>
                <td>
                  {c.name}
                  {c.reversed ? ' · перевёрнута' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Stack>

      <Text variant="caption" tone="tertiary" measure>
        Алгоритм: {proof.algorithm}. Пересчитать может кто угодно — данных
        выше для этого достаточно.
      </Text>
    </Stack>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className={styles.step}>
      <span className={styles.stepN}>{n}</span>
      <Stack gap={2} className={styles.stepBody}>
        <Text variant="label-m">{title}</Text>
        {children}
      </Stack>
    </li>
  )
}

/**
 * Галочка или крест.
 *
 * Значок, а не только цвет: цвет никогда не должен быть единственным
 * носителем смысла — это правило всей системы, и здесь оно особенно
 * важно, потому что речь о доверии.
 */
function Check({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <span className={ok ? styles.checkOk : styles.checkBad}>
      <span aria-hidden>{ok ? '✓' : '✕'}</span>
      <Text variant="body-s" as="span">
        {children}
      </Text>
    </span>
  )
}
